import {
  Button,
  Box,
  HStack,
  VStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { mixed, object } from 'yup';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { DataTape } from 'types/manualDataTapeUpload';

import { DataTapeFormatModal } from './DataTapeFormatModal';
import UploadDataTapeDropzone, {
  UploadFileExtension,
} from './UploadDataTapeDropzone';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

// Helpers
const configFromFileExtensions = (
  allowedExtensions: UploadFileExtension[],
): { [key: string]: string[] } => {
  const ret: { [key: string]: string[] } = {};
  allowedExtensions.forEach((type) => {
    switch (type) {
      case 'csv':
        ret['text/csv'] = ['.csv'];
        break;
      case 'xlsx':
        ret[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.xlsx'];
        break;
      case 'xlsm':
        ret['application/vnd.ms-excel.sheet.macroEnabled.12'] = ['.xlsm'];
        break;
      case 'xlsb':
        ret['application/vnd.ms-excel.sheet.binary.macroEnabled.12'] = [
          '.xlsb',
        ];
        break;
      case 'json':
        ret['application/json'] = ['.json'];
        break;
      case 'pdf':
        ret['application/pdf'] = ['.pdf'];
        break;
      case 'txt':
        ret['text/plain'] = ['.txt', '.TXT'];
        break;
      case 'zip':
        ret['application/zip'] = ['.zip'];
        break;
      default:
        finleyAssertUnreachable(type);
    }
  });
  return ret;
};

// Exports
type Props = {
  allowedExtensions: UploadFileExtension[];
  onClose: () => void;
  handleFile: (file: File) => void;
  dataTape?: DataTape;
};
export const UploadDataTapeForm = ({
  dataTape,
  allowedExtensions,
  onClose,
  handleFile,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [isDataTapeFormatModalOpen, setIsDataTapeFormatModalOpen] = useState(
    false,
  );

  const fontColor = useColorModeValue('gray.600', 'white');
  const buttonColorScheme = useColorModeValue('aurora', 'gray');

  return (
    <Formik
      onSubmit={async (values) => {
        try {
          if (values && values.file) {
            handleFile(values.file);
          }
        } catch (err) {
          setLoading(false);
          throw err;
        }
        setLoading(false);
        onClose();
      }}
      validationSchema={object().shape({
        file: mixed(),
      })}
      initialValues={{
        file: null,
      }}
      validateOnChange={false}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <Form onSubmit={handleSubmit}>
          <VStack spacing="10px">
            {dataTape && (
              <Box alignSelf="flex-start">
                <HStack color={fontColor}>
                  <Text fontWeight="semibold">Data Tape Format</Text>
                  <Button
                    leftIcon={<FaIcon icon="eye" />}
                    variant="ghost"
                    onClick={() => setIsDataTapeFormatModalOpen(true)}
                  >
                    View
                  </Button>
                </HStack>
                <DataTapeFormatModal
                  isOpen={isDataTapeFormatModalOpen}
                  onClose={() => setIsDataTapeFormatModalOpen(false)}
                  dataTape={dataTape}
                />
              </Box>
            )}
            <UploadDataTapeDropzone
              allowedExtensions={allowedExtensions}
              isDisabled={loading}
              setFieldValue={(value: FileWithPath) =>
                setFieldValue(`file`, value)
              }
              accept={configFromFileExtensions(allowedExtensions)}
            />
            <Box py="12px" alignSelf="flex-end">
              <HStack spacing="25px">
                <Button
                  variant="outline"
                  colorScheme={buttonColorScheme}
                  onClick={onClose}
                  px="20px"
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  colorScheme={buttonColorScheme}
                  isDisabled={!values.file}
                  isLoading={loading}
                  type="submit"
                  px="20px"
                >
                  Upload
                </Button>
              </HStack>
            </Box>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};
